import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { get } from 'lodash-es'
import { chatbotEvents } from '../state'
import { portfolioApiEvents } from 'modules/api/portfolio'

export interface PropertyDetailsState {
  selectedProperty: string | null
  selectedPlace: string | null
  selectedLightstoneProperty: string | null
  propertyDescription: string
  buildingName: string
  unitNumber: string
  schemeNumber: string
  schemeName: string
}

export interface GooglePlaceSelectedPayload {
  placeId?: string
  id?: string
  propertyDescription?: string
  buildingName?: string
  unitNumber?: string
  schemeNumber?: string
  schemeName?: string
}

export interface PropertySelectedPayload {
  id: string
  propertyDescription?: string
  buildingName?: string
  unitNumber?: string
  schemeNumber?: string
  schemeName?: string
}

export interface LightstonePropertySelectedPayload {
  lightstoneId: string
  propertyDescription?: string
  buildingName?: string
  unitNumber?: string
  schemeNumber?: string
  schemeName?: string
}

export const initialState: PropertyDetailsState = {
  selectedProperty: null,
  selectedPlace: null,
  selectedLightstoneProperty: null,
  propertyDescription: '',
  buildingName: '',
  unitNumber: '',
  schemeNumber: '',
  schemeName: '',
}

const propertyDetailsSlice = createSlice({
  name: 'chatbot/dialogs/propertyDetails',
  initialState,
  reducers: {
    googlePlaceSelected: (state, action: PayloadAction<GooglePlaceSelectedPayload>) => {
      const { id, placeId, propertyDescription, buildingName, unitNumber, schemeNumber, schemeName } = action.payload
      state.selectedProperty = null
      // CRITICAL: Only set selectedPlace when placeId defined AND id NOT defined
      // This prevents setting selectedPlace when propertySelected event fires
      state.selectedPlace = placeId !== undefined && id === undefined ? placeId : null
      state.selectedLightstoneProperty = null
      state.propertyDescription = propertyDescription ?? ''
      state.buildingName = buildingName ?? ''
      state.unitNumber = unitNumber ?? ''
      state.schemeNumber = schemeNumber ?? ''
      state.schemeName = schemeName ?? ''
    },
    propertySelected: (state, action: PayloadAction<PropertySelectedPayload>) => {
      const { id, propertyDescription, buildingName, unitNumber, schemeNumber, schemeName } = action.payload
      state.selectedProperty = id
      state.selectedPlace = null
      state.selectedLightstoneProperty = null
      state.propertyDescription = propertyDescription ?? ''
      state.buildingName = buildingName ?? ''
      state.unitNumber = unitNumber ?? ''
      state.schemeNumber = schemeNumber ?? ''
      state.schemeName = schemeName ?? ''
    },
    lightstonePropertySelected: (state, action: PayloadAction<LightstonePropertySelectedPayload>) => {
      const { lightstoneId, propertyDescription, buildingName, unitNumber, schemeNumber, schemeName } = action.payload
      state.selectedProperty = null
      state.selectedPlace = null
      state.selectedLightstoneProperty = lightstoneId
      state.propertyDescription = propertyDescription ?? ''
      state.buildingName = buildingName ?? ''
      state.unitNumber = unitNumber ?? ''
      state.schemeNumber = schemeNumber ?? ''
      state.schemeName = schemeName ?? ''
    },
    clearSelection: state => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(portfolioApiEvents.amendProperty_success, (state, action) => {
        state.selectedProperty = action.payload.propertyId ?? null
        state.selectedPlace = null
        // buildingName/unitNumber/schemeNumber/schemeName live on the *property*, not on the
        // portfolio this event carries, so these were only ever being assigned `undefined`
        // (which also broke the declared `string` type and un-controlled the inputs).
        state.buildingName = ''
        state.unitNumber = ''
        state.schemeNumber = ''
        state.schemeName = ''
      })
      .addCase(portfolioApiEvents.amendMetaData_success, (state, action) => {
        const payload = action.payload as { propertyDescription?: string }
        state.propertyDescription = payload.propertyDescription ?? ''
      })
      .addCase(chatbotEvents.setCurrentPortfolio, (state, action) => {
        state.selectedProperty = get(action.payload, 'propertyId', null)
      })
      .addCase(chatbotEvents.resetCurrentPortfolio, state => {
        Object.assign(state, initialState)
      })
  },
})

export const { reducer } = propertyDetailsSlice
export const propertyDetailsEvents = propertyDetailsSlice.actions
